import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { AnalyticsStatus, useAnalytics } from '@treams/treams-analytics';
import { GroupsIcon, HistoryIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Card,
	Checkbox,
	Container,
	Empty,
	Heading3,
	type ITimeMachine,
	Jumbo,
	Skeleton,
	Tabs,
	createTimeMachine,
} from '@treams/treams-ui';

import { AnalyticsFilter } from '../components/AnalyticsFilter';
import {
	type ScoreCardFactors,
	ScoreCategory,
	ScoreModuleState,
	type ScoreModuleStatus,
	type TreamsCategoryScore,
	type TreamsScore,
} from './TreamsScore.types';
import { TreamsScoreAnalyticsLoading } from './TreamsScoreAnalyticsLoading';
import { TreamsScoreCard } from './TreamsScoreCard/TreamsScoreCard';
import { TreamsScoreTable } from './TreamsScoreTable/TreamsScoreTable';
import { type ScoreColumnId, getEdgeCount } from './TreamsScoreTable/cells';
import { getEngagementReliabilityDaysRemaining } from './config/engagementReliability';
import { useScoreGroupFilter } from './hooks/useScoreGroupFilter';
import { useScoreModuleStatus } from './hooks/useScoreModuleStatus';
import { useScoreColors, useTreamsScore } from './hooks/useTreamsScore';
import { adjustScoresForModuleStatus } from './scoreTotal';

type ScoreGroupType = graphql.GroupType.Department | graphql.GroupType.Team;

// Shared between the comparison tabs and the first table column: the column
// title follows the active tab by definition.
const GROUP_TYPE_LABEL_KEYS: Record<ScoreGroupType, string> = {
	[graphql.GroupType.Department]: 'analytics.treamsScore.column.departments',
	[graphql.GroupType.Team]: 'analytics.treamsScore.column.teams',
};

const FACTOR_LABEL_KEYS: Record<ScoreCategory, string> = {
	[ScoreCategory.Engagement]: 'analytics.treamsScore.factor.engagement',
	[ScoreCategory.Targets]: 'analytics.treamsScore.factor.targets',
	[ScoreCategory.Feedback]: 'analytics.treamsScore.factor.feedback',
	[ScoreCategory.OneOnOne]: 'analytics.treamsScore.factor.oneOnOne',
	[ScoreCategory.Conversations]: 'analytics.treamsScore.factor.conversations',
};

export function TreamsScoreAnalytics(props: { headerFooter?: ReactNode }) {
	const { t } = useTranslation();

	const factorLabels = useMemo<Record<ScoreCategory, string>>(
		() =>
			Object.fromEntries(
				(Object.keys(FACTOR_LABEL_KEYS) as ScoreCategory[]).map((category) => [
					category,
					t(FACTOR_LABEL_KEYS[category]),
				]),
			) as Record<ScoreCategory, string>,
		[t],
	);

	const scoreColumnLabels = useMemo<Record<ScoreColumnId, string>>(
		() => ({ total: t('analytics.treamsScore.column.total'), ...factorLabels }),
		[t, factorLabels],
	);

	const [timeMachine, setTimeMachine] = useState<ITimeMachine>(() =>
		createTimeMachine('month', new Date()),
	);

	const endDate = timeMachine.current[1];

	const {
		rows: teams,
		isLoading: scoreLoading,
		engagementSurveyAvailable,
		engagementFirstSurveyAt,
	} = useTreamsScore(endDate);

	const { status, loading: analyticsLoading } = useAnalytics();

	const analyticsUnavailable = !analyticsLoading && status !== AnalyticsStatus.Succeeded;

	// Keep the skeleton up while the score worker request is still in flight.
	const loading = analyticsLoading || (status === AnalyticsStatus.Succeeded && scoreLoading);

	const scoreColors = useScoreColors();

	const [groupType, setGroupType] = useState<ScoreGroupType>(graphql.GroupType.Department);

	const filter = useScoreGroupFilter(teams);

	const baseModuleStatus = useScoreModuleStatus();

	// A module that is switched off today should not blank out history: when a
	// qualifying survey was available in the selected window, engagement scores
	// like an enabled module; the disabled fallback (score 1) only applies when
	// no survey ran at that moment.
	const moduleStatus = useMemo<ScoreModuleStatus>(
		() =>
			baseModuleStatus[ScoreCategory.Engagement] === ScoreModuleState.Disabled &&
			engagementSurveyAvailable
				? { ...baseModuleStatus, [ScoreCategory.Engagement]: ScoreModuleState.Enabled }
				: baseModuleStatus,
		[baseModuleStatus, engagementSurveyAvailable],
	);

	const aggregatedScores = useMemo<TreamsScore>(() => {
		const rows = filter.filteredRows;
		const categories = Object.values(ScoreCategory);

		const mean = (values: number[]) =>
			values.length ? values.reduce((total, value) => total + value, 0) / values.length : 0;

		const categoryScores = Object.fromEntries(
			categories.map((category) => [
				category,
				{
					score: mean(rows.map((row) => row.scores[category].score)),
					delta: mean(rows.map((row) => row.scores[category].delta)),
				},
			]),
		) as Record<ScoreCategory, TreamsCategoryScore>;

		return { ...categoryScores, total: { score: 0, delta: 0 } };
	}, [filter.filteredRows]);

	const hasData = useMemo(() => {
		return filter.filteredRows.length > 0;
	}, [filter.filteredRows]);

	const adjustedScores = useMemo(
		() => adjustScoresForModuleStatus(aggregatedScores, moduleStatus),
		[aggregatedScores, moduleStatus],
	);

	const adjustedRows = useMemo(
		() =>
			filter.filteredRows.map((row) => ({
				...row,
				scores: adjustScoresForModuleStatus(row.scores, moduleStatus),
			})),
		[filter.filteredRows, moduleStatus],
	);

	const [compactView, setCompactView] = useState(false);

	// The comparison tabs only narrow the table; the score card above stays
	// aggregated over the full (selection-filtered) set.
	const tableRows = useMemo(
		() => adjustedRows.filter((row) => row.type === groupType),
		[adjustedRows, groupType],
	);

	const edgeCount = getEdgeCount(tableRows.length);
	const canCompact = edgeCount > 0 && tableRows.length > edgeCount * 2;

	const engagementDaysRemaining = useMemo(
		() => getEngagementReliabilityDaysRemaining(engagementFirstSurveyAt, endDate),
		[engagementFirstSurveyAt, endDate],
	);

	const scoreFactors = useMemo<ScoreCardFactors>(
		() =>
			Object.fromEntries(
				(Object.keys(FACTOR_LABEL_KEYS) as ScoreCategory[]).map((category) => [
					category,
					{
						label: factorLabels[category],
						score: adjustedScores[category].score,
						state: moduleStatus[category],
					},
				]),
			) as ScoreCardFactors,
		[factorLabels, adjustedScores, moduleStatus],
	);

	return (
		<>
			<Jumbo title={t('analytics.treamsScore.pageTitle')} footer={props.headerFooter} wide={true} />

			<>
				<AnalyticsFilter
					departments={filter}
					canSelectGroup={filter.canSelect}
					onDepartmentChange={filter.updateExplicitGroupSelection}
					timeMachine={timeMachine}
					onTimeMachineChange={setTimeMachine}
					disableQuery
					allowFutureDates
				/>

				<Container
					wide={true}
					py={4}
					maxHeight={!loading && (analyticsUnavailable || !hasData) ? '500px' : undefined}
					overflow={!loading && (analyticsUnavailable || !hasData) ? 'hidden' : undefined}
				>
					{loading ? (
						<TreamsScoreAnalyticsLoading />
					) : analyticsUnavailable || !hasData ? (
						<Box style={{ position: 'relative', height: '500px' }}>
							<Skeleton.Empty contents={null}>
								<TreamsScoreAnalyticsLoading />
							</Skeleton.Empty>
							<Box
								style={{
									position: 'absolute',
									bottom: 0,
									left: 0,
									width: '100%',
									height: '400px',
									background: `linear-gradient(to bottom, transparent, white)`,
									pointerEvents: 'none',
								}}
							/>
							<Box
								style={{
									zIndex: 4,
									position: 'absolute',
									left: '50%',
									top: '50%',
									transform: 'translate(-50%, -70%)',
								}}
							>
								{analyticsUnavailable ? (
									<Empty
										icon={<HistoryIcon />}
										title={t('analytics.cycles.notReady.title')}
										description={t('analytics.cycles.notReady.description')}
									/>
								) : (
									<Empty
										icon={<GroupsIcon />}
										title={t('analytics.treamsScore.empty.title')}
										description={t('analytics.treamsScore.empty.description')}
									/>
								)}
							</Box>
						</Box>
					) : (
						<>
							<TreamsScoreCard
								factors={scoreFactors}
								total={adjustedScores.total}
								getScoreColors={scoreColors}
								engagementDaysRemaining={engagementDaysRemaining}
							/>
							<Card mt={4}>
								<Flex alignItems="center" justifyContent="space-between">
									<Heading3>{t('analytics.treamsScore.teamComparison')}</Heading3>
									{canCompact && (
										<Checkbox
											checked={compactView}
											label={t('analytics.treamsScore.table.showOnlyTopBottom', {
												count: edgeCount,
											})}
											onClick={() => setCompactView((current) => !current)}
										/>
									)}
								</Flex>
								<Box mt={3}>
									<Tabs>
										<Flex ml={-3}>
											<Tabs.Item
												title={t(GROUP_TYPE_LABEL_KEYS[graphql.GroupType.Department])}
												selected={groupType === graphql.GroupType.Department}
												onClick={() => setGroupType(graphql.GroupType.Department)}
											/>
											<Tabs.Item
												title={t(GROUP_TYPE_LABEL_KEYS[graphql.GroupType.Team])}
												selected={groupType === graphql.GroupType.Team}
												onClick={() => setGroupType(graphql.GroupType.Team)}
											/>
										</Flex>
									</Tabs>
								</Box>
								<TreamsScoreTable
									teams={tableRows}
									columnLabels={scoreColumnLabels}
									moduleStatus={moduleStatus}
									getScoreColors={scoreColors}
									compact={compactView}
									nameColumnHeader={t(GROUP_TYPE_LABEL_KEYS[groupType])}
									emptyState={{
										icon: <GroupsIcon />,
										title: t('analytics.treamsScore.empty.title'),
										description: t('analytics.treamsScore.empty.description'),
									}}
								/>
							</Card>
						</>
					)}
				</Container>
			</>
		</>
	);
}
